import { Button } from '@/components/ui/shadcn/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/shadcn/dialog'
import { Columns2, Edit, FlaskConical, Rows2 } from 'lucide-react'
import { useState, useMemo } from 'react'
import {
  DropDownArrowIcon,
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  MetaAdsIcon,
  TikTokIcon,
  YoutubeIcon,
} from '@/components/ui/icons/icons-new'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { type WorkspaceCaptions } from '@backend/lib/db/schema/workspace'
import { toast } from 'sonner'
import { diffWords } from 'diff'

interface CaptionSuggestionInput extends WorkspaceCaptions {
  youtubeTitle?: string
}

interface Props {
  captions: CaptionSuggestionInput
  currentCaptions: WorkspaceCaptions
  onApply: (caption: string, platform: PlatformType, title?: string) => void
}

// Platform configuration
const platformConfig = {
  instagram: {
    Icon: InstagramIcon,
    name: 'Instagram',
    bgColor: 'bg-pink-500',
  },
  linkedin: {
    Icon: LinkedinIcon,
    name: 'LinkedIn',
    bgColor: 'bg-blue-600',
  },
  tiktok: {
    Icon: TikTokIcon,
    name: 'TikTok',
    bgColor: 'bg-black',
  },
  youtube: {
    Icon: YoutubeIcon,
    name: 'YouTube',
    bgColor: 'bg-red-600',
  },
  facebook: {
    Icon: FacebookIcon,
    name: 'Facebook',
    bgColor: 'bg-[#1877F2]',
  },
  'meta-ads': {
    Icon: MetaAdsIcon,
    name: 'Meta Ads',
    bgColor: 'bg-[#0866FF]',
  },
} as const

function CaptionDiffInline({ oldText, newText }: { oldText: string; newText: string }) {
  const changes = useMemo(() => diffWords(oldText, newText), [oldText, newText])

  return (
    <div className="whitespace-pre-wrap font-mono text-sm leading-relaxed">
      {changes.map((change, i) => {
        if (change.added) {
          return (
            <span key={i} className="rounded-sm bg-green-500/20 text-green-700 dark:text-green-400">
              {change.value}
            </span>
          )
        }
        if (change.removed) {
          return (
            <span
              key={i}
              className="rounded-sm bg-red-500/20 text-red-700 line-through dark:text-red-400">
              {change.value}
            </span>
          )
        }
        return <span key={i}>{change.value}</span>
      })}
    </div>
  )
}

function CaptionDiffSideBySide({ oldText, newText }: { oldText: string; newText: string }) {
  const changes = useMemo(() => diffWords(oldText, newText), [oldText, newText])

  const oldParts: React.ReactNode[] = []
  const newParts: React.ReactNode[] = []

  changes.forEach((change, i) => {
    if (change.added) {
      newParts.push(
        <span key={i} className="rounded-sm bg-green-500/20 text-green-700 dark:text-green-400">
          {change.value}
        </span>,
      )
    } else if (change.removed) {
      oldParts.push(
        <span
          key={i}
          className="rounded-sm bg-red-500/20 text-red-700 line-through dark:text-red-400">
          {change.value}
        </span>,
      )
    } else {
      oldParts.push(<span key={`old-${i}`}>{change.value}</span>)
      newParts.push(<span key={`new-${i}`}>{change.value}</span>)
    }
  })

  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <p className="text-muted-foreground mb-2 text-xs font-medium">Current</p>
        <div className="whitespace-pre-wrap rounded-md bg-red-500/10 p-3 font-mono text-sm leading-relaxed">
          {oldParts.length > 0 ? (
            oldParts
          ) : (
            <span className="text-muted-foreground italic">Empty</span>
          )}
        </div>
      </div>
      <div>
        <p className="text-muted-foreground mb-2 text-xs font-medium">Suggested</p>
        <div className="whitespace-pre-wrap rounded-md bg-green-500/10 p-3 font-mono text-sm leading-relaxed">
          {newParts.length > 0 ? (
            newParts
          ) : (
            <span className="text-muted-foreground italic">Empty</span>
          )}
        </div>
      </div>
    </div>
  )
}

export function WorkspaceChatPlatformCaptionCard({ captions, currentCaptions, onApply }: Props) {
  const [expandedPlatforms, setExpandedPlatforms] = useState<Record<PlatformType, boolean>>({
    instagram: true,
    linkedin: true,
    tiktok: true,
    youtube: true,
    facebook: true,
    'meta-ads': true,
  })
  const [diffPlatform, setDiffPlatform] = useState<PlatformType | null>(null)
  const [diffMode, setDiffMode] = useState<'inline' | 'side-by-side'>('inline')

  // Filter against platformConfig keys to exclude non-platform fields
  // like youtubeTitle that live on the same object.
  const availablePlatforms = useMemo(() => {
    return (Object.keys(platformConfig) as PlatformType[]).filter(
      (platform) => captions[platform] && captions[platform]!.trim().length > 0,
    )
  }, [captions])

  const [activeTab, setActiveTab] = useState<PlatformType | undefined>(availablePlatforms[0])

  if (availablePlatforms.length === 0) {
    return null
  }

  const toggleExpanded = (platform: PlatformType) => {
    setExpandedPlatforms((prev) => ({ ...prev, [platform]: !prev[platform] }))
  }

  return (
    <>
      <div className="mt-3">
        <div className="bg-card rounded-lg">
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as PlatformType)}
            className="w-full">
            <div className="bg-card rounded-t-lg p-2">
              <TabsList className="w-full">
                {availablePlatforms.map((platform) => {
                  const config = platformConfig[platform]
                  const Icon = config.Icon
                  return (
                    <TabsTrigger
                      key={platform}
                      value={platform}
                      className="flex flex-1 items-center gap-1.5">
                      <span
                        className={`flex items-center justify-center rounded-full ${config.bgColor} p-1`}>
                        <Icon width={12} height={12} color="white" />
                      </span>
                      <span className="text-xs">{config.name}</span>
                    </TabsTrigger>
                  )
                })}
              </TabsList>
            </div>

            {availablePlatforms.map((platform) => {
              const caption = captions[platform]!
              const suggestedTitle = platform === 'youtube' ? captions.youtubeTitle : undefined
              const isExpanded = expandedPlatforms[platform]
              const needsTruncation = caption.length > 100 || caption.split('\n').length > 2

              return (
                <TabsContent key={platform} value={platform} className="mt-0">
                  <div className="flex items-center justify-between gap-2 px-2 py-1">
                    <p className="text-xs font-light text-green-500">Finished!</p>
                    {needsTruncation && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => toggleExpanded(platform)}
                        className="h-6 text-xs">
                        {isExpanded ? 'Show less' : 'Show more'}
                        <DropDownArrowIcon
                          width={12}
                          height={12}
                          color="var(--foreground)"
                          direction={isExpanded ? 'up' : 'down'}
                        />
                      </Button>
                    )}
                  </div>
                  <div
                    className="overflow-hidden transition-[max-height] duration-300 ease-in-out"
                    style={{ maxHeight: isExpanded ? '1000px' : '3em' }}>
                    {suggestedTitle && (
                      <div className="border-border/60 mx-2 mb-1 border-b pb-1">
                        <p className="text-muted-foreground text-xs">Title</p>
                        <p className="text-sm font-semibold">{suggestedTitle}</p>
                      </div>
                    )}
                    <div className="whitespace-pre-wrap px-2 pb-2 text-sm">{caption}</div>
                  </div>
                  <div className="bg-card flex items-center justify-end gap-2 rounded-b-lg p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDiffPlatform(platform)}
                      className="text-muted-foreground text-xs">
                      <FlaskConical className="h-3.5 w-3.5" />
                      Diff
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        onApply(caption, platform, suggestedTitle)
                        setExpandedPlatforms((prev) => ({ ...prev, [platform]: false }))
                        toast.success(`Caption applied for ${platformConfig[platform].name}`)
                      }}>
                      Apply <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                </TabsContent>
              )
            })}
          </Tabs>
        </div>
      </div>

      <Dialog open={diffPlatform !== null} onOpenChange={() => setDiffPlatform(null)}>
        <DialogContent
          className={`max-h-[80vh] overflow-y-auto ${diffMode === 'side-by-side' ? 'max-w-2xl' : 'max-w-lg'}`}>
          <DialogHeader>
            <div className="flex items-center justify-between pr-6">
              <DialogTitle>
                Caption changes{' '}
                {diffPlatform && (
                  <span className="text-muted-foreground font-normal">
                    — {platformConfig[diffPlatform].name}
                  </span>
                )}
              </DialogTitle>
              <div className="border-border flex rounded-md border">
                <button
                  onClick={() => setDiffMode('inline')}
                  className={`rounded-l-md p-1.5 ${diffMode === 'inline' ? 'bg-muted' : 'hover:bg-hover transition-colors'}`}
                  title="Inline diff">
                  <Rows2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setDiffMode('side-by-side')}
                  className={`rounded-r-md p-1.5 ${diffMode === 'side-by-side' ? 'bg-muted' : 'hover:bg-hover transition-colors'}`}
                  title="Side by side">
                  <Columns2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </DialogHeader>
          {diffPlatform && (
            <>
              {diffMode === 'inline' ? (
                <div className="bg-muted rounded-md p-3">
                  <CaptionDiffInline
                    oldText={currentCaptions[diffPlatform] || ''}
                    newText={captions[diffPlatform] || ''}
                  />
                </div>
              ) : (
                <CaptionDiffSideBySide
                  oldText={currentCaptions[diffPlatform] || ''}
                  newText={captions[diffPlatform] || ''}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
